import React, { useMemo } from "react";
import styled from "styled-components";
import { border, content } from "@glossy";
import { Modal } from "@linkfluence/core/modal";
import { Locale } from "@linkfluence/models/locale";
import InnerModal from "@ui-kit/modal";
import { tr } from "@radarly/i18n";
import {
  CashType,
  countriesEMV,
  defaultEMV,
} from "@radarly/models/cards/types/emv";

const Content = styled.div`
  overflow-y: scroll;
  max-height: 500px;
`;

const Table = styled.table`
  border-collapse: collapse;
  border: 1px solid ${border};
  font-family: sans-serif;
  letter-spacing: 1px;
`;

const TBody = styled.tbody`
  > tr:nth-of-type(even) {
    background-color: rgb(237 238 242);
  }
`;

const TH = styled.th`
  border: 1px solid rgb(160 160 160);
  padding: 8px 10px;
  font-weight: 700;
`;

const TD = styled.td`
  border: 1px solid rgb(160 160 160);
  padding: 8px 10px;
`;

const Notes = styled.div`
  margin-top: 12px;
`;

const Note = styled.p`
  color: ${content};
  font-size: 8px;
`;

export default function useEmvRatioListModal(locale: Locale, modaleID: string) {
  const { open, close, ModalWrapper } = Modal.get(modaleID);
  const countries = useMemo(
    () =>
      Object.entries(countriesEMV)
        .map(([country, scores]) => ({
          label: tr(locale, `COUNTRIES::${country}`),
          scores: {
            print:
              scores[CashType.Print] === 0
                ? "N/A"
                : `${scores[CashType.Print]}€`,
            social: `${scores[CashType.Social]}€`,
            web: `${scores[CashType.Web]}€`,
          },
        }))
        .sort((a, b) => (a.label > b.label ? 1 : -1)),
    [locale]
  );

  const EmvRatioModal = () => (
    <ModalWrapper>
      <InnerModal
        title={tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_TITLE")}
        onCancel={() => close()}
        cancelLabel={tr(locale, "MODAL::CLOSE")}
        locale={locale}
        width="auto"
      >
        <Content>
          <Table>
            <thead>
              <tr>
                <TH>
                  {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_COUNTRY")}
                </TH>
                <TH>
                  {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_OFFLINE")}
                </TH>
                <TH>
                  {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_SOCIAL")}
                </TH>
                <TH>
                  {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_ONLINE")}
                </TH>
              </tr>
            </thead>

            <TBody>
              {countries.map(({ label, scores }) => (
                <tr>
                  <TD scope="row">{label}</TD>
                  <TD>{scores.print}</TD>
                  <TD>{scores.social}</TD>
                  <TD>{scores.web}</TD>
                </tr>
              ))}
              <tr>
                <TD scope="row">{tr(locale, "FIELD_VALUES::GLOBAL")}</TD>
                <TD>{defaultEMV[CashType.Print]}€</TD>
                <TD>{defaultEMV[CashType.Social]}€</TD>
                <TD>{defaultEMV[CashType.Web]}€</TD>
              </tr>
            </TBody>
          </Table>
          <Notes>
            <Note>
              {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_NOTEOFFLINE")}
            </Note>
            <Note>
              {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_NOTESOCIAL")}
            </Note>
            <Note>
              {tr(locale, "CUSTOM_PAGE::EDITOR::MODAL::RATIO_LIST_NOTEONLINE")}
            </Note>
          </Notes>
        </Content>
      </InnerModal>
    </ModalWrapper>
  );
  return {
    EmvRatioModal,
    onOpen: open,
  };
}
